import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  EXPERIMENT_TYPES, METRICS, fetchExperiments, createExperiment, fetchResults, declareWinner, removeExperiment,
} from "../../services/experimentsService";

const STATUS = { running: "bg-green-100 text-green-700", paused: "bg-amber-100 text-amber-700", completed: "bg-gray-100 text-gray-600" };

const NewModal = ({ onClose, onCreated }) => {
  const [d, setD] = useState({ name: "", type: "creative", metric: "cvr", vA: "Variante A", vB: "Variante B" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!d.name.trim()) return toast.warn("Nommez l'expérience.");
    setBusy(true);
    try { await createExperiment({ name: d.name.trim(), type: d.type, metric: d.metric, variants: [{ name: d.vA }, { name: d.vB }] }); toast.success("Test créé"); onCreated(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.message || "Création impossible."); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b"><h3 className="font-bold text-[#221C51]">Nouveau test A/B</h3><button onClick={onClose} className="text-gray-400 text-xl">×</button></div>
        <div className="p-6 space-y-4">
          <input value={d.name} onChange={(e) => set("name", e.target.value)} placeholder="Créa UGC vs Studio" className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-[#221C51]" />
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1">Type</p>
            <div className="flex flex-wrap gap-2">{EXPERIMENT_TYPES.map((t) => <button key={t.value} onClick={() => set("type", t.value)} className={`px-3 py-1.5 rounded-lg text-sm border ${d.type === t.value ? "bg-[#221C51] text-white border-[#221C51]" : "border-gray-200 text-gray-600"}`}>{t.label}</button>)}</div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1">Métrique gagnante</p>
            <div className="flex gap-2">{METRICS.map(([k, l]) => <button key={k} onClick={() => set("metric", k)} className={`px-3 py-1.5 rounded-lg text-sm border ${d.metric === k ? "bg-[#221C51] text-white border-[#221C51]" : "border-gray-200 text-gray-600"}`}>{l}</button>)}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input value={d.vA} onChange={(e) => set("vA", e.target.value)} className="px-3 py-2 text-sm border border-gray-300 rounded-lg" />
            <input value={d.vB} onChange={(e) => set("vB", e.target.value)} className="px-3 py-2 text-sm border border-gray-300 rounded-lg" />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg">Annuler</button>
          <button onClick={submit} disabled={busy} className="px-5 py-2 text-sm text-white bg-[#221C51] rounded-lg font-semibold disabled:opacity-50">{busy ? "Création…" : "Créer le test"}</button>
        </div>
      </div>
    </div>
  );
};

const ExperimentCard = ({ exp, onDeclare, onRemove }) => {
  const [res, setRes] = useState(null);
  useEffect(() => { fetchResults(exp.id).then(setRes).catch(() => {}); }, [exp.id]);
  const val = (v) => exp.metric === "cvr" ? `${v.cvr}%` : exp.metric === "roas" ? `${v.roas}×` : `${v.cpa} €`;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between">
        <div><p className="font-bold text-[#221C51]">{exp.name}</p><p className="text-xs text-gray-400">{exp.type} · métrique {exp.metric}</p></div>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${exp.winner ? "bg-green-100 text-green-700" : STATUS[exp.status]}`}>{exp.winner ? `🏆 ${exp.winner}` : exp.status}</span>
      </div>
      {res && (
        <>
          <div className="grid md:grid-cols-2 gap-3 mt-4">
            {res.variants.map((v) => {
              const leader = res.leader === v.key;
              return (
                <div key={v.key} className={`rounded-xl border p-3 ${leader ? "border-green-400 bg-green-50" : "border-gray-200"}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-bold text-sm text-[#221C51]">{v.key} · {v.name}{leader ? " 👑" : ""}</p>
                    <p className={`font-extrabold ${leader ? "text-green-700" : "text-gray-800"}`}>{val(v)}</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{v.clicks} clics · {v.conversions} conv · {v.revenue} €</p>
                  {exp.status !== "completed" && <button onClick={() => onDeclare(exp.id, v.key)} className="mt-2 text-xs font-semibold text-white bg-green-600 rounded px-2.5 py-1 hover:bg-green-700">Désigner gagnant</button>}
                </div>
              );
            })}
          </div>
          <div className={`mt-3 text-sm rounded-lg px-3 py-2 flex items-center gap-2 ${res.canDecide ? "bg-green-50 text-green-800" : "bg-gray-50 text-gray-600"}`}>
            <span>{res.canDecide ? "✅" : "⏳"}</span>{res.reason}
          </div>
        </>
      )}
      <div className="flex justify-end mt-3"><button onClick={() => onRemove(exp)} className="text-xs text-gray-400 hover:text-red-500">Supprimer</button></div>
    </div>
  );
};

const Experiments = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const load = useCallback(async () => { try { setItems(await fetchExperiments() || []); } catch {} finally { setLoading(false); } }, []);
  useEffect(() => { load(); }, [load]);

  const onDeclare = async (id, winner) => { try { await declareWinner(id, winner); toast.success(`Gagnant : ${winner} — coupez le perdant.`); load(); } catch (e) { toast.error(e?.response?.data?.message || "Échec."); } };
  const onRemove = async (exp) => { if (!window.confirm(`Supprimer « ${exp.name} » ?`)) return; try { await removeExperiment(exp.id); setItems((p) => p.filter((x) => x.id !== exp.id)); } catch {} };

  return (
    <div className="p-2 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div><h2 className="text-2xl font-bold text-[#221C51]">A/B Testing</h2><p className="text-sm text-gray-400 mt-0.5">Testez, mesurez la significativité, gardez le gagnant.</p></div>
        <button onClick={() => setModal(true)} className="px-4 py-2.5 bg-[#221C51] text-white text-sm font-semibold rounded-xl hover:bg-[#2e265e]">＋ Nouveau test A/B</button>
      </div>
      {loading ? <p className="text-gray-400 py-16 text-center">Chargement…</p>
        : items.length === 0 ? <div className="text-center py-20"><div className="text-5xl mb-3">⚗️</div><p className="font-semibold text-[#221C51]">Aucun test</p><p className="text-sm text-gray-400 mt-1">Lancez votre premier A/B test.</p></div>
        : <div className="grid gap-4">{items.map((exp) => <ExperimentCard key={exp.id} exp={exp} onDeclare={onDeclare} onRemove={onRemove} />)}</div>}
      {modal && <NewModal onClose={() => setModal(false)} onCreated={load} />}
    </div>
  );
};

export default Experiments;
